import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import "./EcoVersePage.css";

/**
 * THE ECOVERSE — the dedicated home of Earth Jr. and the Eco-Force.
 * Characters, films, games, comics, collectibles and free downloads.
 * The kids-and-families gateway, so the main site stays focused on services.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const CHARACTERS = [
  { img: "earthjr-stand.png", name: "EARTH JR.", role: "The Hero", bio: "Brave, hopeful, Brooklyn-born. He believes everything — and everyone — deserves a second chance." },
  { img: "binny_hero.png", name: "BINNY", role: "The Collector", bio: "Funny and tireless. Collects and sorts everything that can be reused. Nothing gets past his lid." },
  { img: "drip_hero.png", name: "DRIP", role: "The Brain", bio: "Smart and cool under pressure. Keeps our water — and the crew's plans — crystal clear." },
  { img: "sunny_hero.png", name: "SUNNY", role: "The Inventor", bio: "Harnesses the power of the sun to fuel the team. Always building something bright." },
  { img: "windy_hero.png", name: "WINDY", role: "The Speedster", bio: "Fast, breezy, always in motion. Keeps the air clean and the team moving." },
  { img: "sprout_hero.png", name: "SPROUT", role: "The Protector", bio: "Strong and wise. Protects nature and all living things — roots deep, heart bigger." },
];

const VILLAINS = [
  { img: "trashbot_mk1.png", name: "TRASH-BOT MK I", tag: "Chapter 1" },
  { img: "trashbot_mk2.png", name: "TRASH-BOT MK II", tag: "Coming" },
  { img: "mega_trashbot.png", name: "MEGA TRASH-BOT", tag: "Coming" },
  { img: "landfill_titan.png", name: "LANDFILL TITAN", tag: "Coming" },
  { img: "king_trashbot.png", name: "KING TRASH-BOT", tag: "The Finale" },
];

const DOWNLOADS = [
  { img: "earthjr-fly.png", label: "Earth Jr. — Flying" },
  { img: "earthjr-thumbs.png", label: "Earth Jr. — Thumbs Up" },
  { img: "scene-williamsburg.png", label: "Williamsburg 2045 Scene" },
  { img: "comic-vs.png", label: "Earth Jr. VS Trash-Bot" },
];

const EcoVersePage: React.FC = () => {
  useReveal();
  const heroVid = useRef<HTMLVideoElement>(null);
  const [clipSound, setClipSound] = useState<string | null>(null);

  // hero video: play only while visible
  useEffect(() => {
    const v = heroVid.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause())),
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // film clips: pause whenever they scroll out of view (saves 3 video decodes)
  const filmsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = filmsRef.current;
    if (!root) return;
    const vids = Array.from(root.querySelectorAll("video"));
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        const v = e.target as HTMLVideoElement;
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      }),
      { threshold: 0.2 }
    );
    vids.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, []);

  return (
    <div className="ev-page">
      {/* ── Hero: Earth Jr. flight film ── */}
      <section className="ev-hero">
        <video
          ref={heroVid}
          className="ev-hero-video"
          src={`${PUBLIC}/ecoverse-hero.mp4`}
          poster={`${PUBLIC}/ecoverse-hero-poster.jpg`}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="ev-hero-shade" />
        <div className="ev-hero-content">
          <div className="ev-kicker">★ WELCOME TO THE ★</div>
          <h1 className="ev-title">ECOVERSE</h1>
          <p className="ev-sub">
            The world of Earth Jr. and the Eco-Force — heroes, games, comics and collectibles.
            Where saving the planet is the greatest adventure in New York.
          </p>
          <div className="ev-hero-btns">
            <Link to="/game" className="ee-btn ee-btn-orange">🎮 Play the Games</Link>
            <a href="#characters" className="ee-btn ev-btn-ghost">Meet the Heroes ↓</a>
          </div>
        </div>
      </section>

      {/* ── Characters ── */}
      <section id="characters" className="ee-section ev-chars">
        <div className="ee-container">
          <div className="ev-sec-kicker">THE ECO-FORCE</div>
          <h2 className="ev-sec-title">Six heroes. One mission.</h2>
          <div className="ev-char-grid">
            {CHARACTERS.map((c, i) => (
              <div className="ev-char-card" key={c.name} style={{ animationDelay: `${i * 0.3}s` }}>
                <img src={`${PUBLIC}/${c.img}`} alt={`${c.name} — ${c.role}`} />
                <div className="ev-char-role">{c.role}</div>
                <strong>{c.name}</strong>
                <p>{c.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Films ── */}
      <section className="ee-section ev-films">
        <div className="ee-container">
          <div className="ev-sec-kicker">NOW SHOWING</div>
          <h2 className="ev-sec-title">Eco-Force Films 🎬</h2>
          <p className="ev-sec-sub">Tap a film for sound.</p>
          <div className="ev-film-grid" ref={filmsRef}>
            {[
              { src: "eco-force-teaser.mp4", poster: "eco-force-teaser-poster.jpg", title: "Official Teaser", tag: "EARTH JR. & THE ECO-FORCE", portrait: true },
              { src: "clip-binny.mp4", poster: "", title: "Binny on the Job", tag: "CHARACTER SHORT", portrait: false },
              { src: "clip-sprout.mp4", poster: "", title: "Sprout Protects", tag: "CHARACTER SHORT", portrait: false },
            ].map((f) => (
              <div
                key={f.src}
                className={`ev-film-card${f.portrait ? " portrait" : ""}${clipSound === f.src ? " sound-on" : ""}`}
                onClick={() => setClipSound(clipSound === f.src ? null : f.src)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setClipSound(clipSound === f.src ? null : f.src); } }}
                role="button"
                tabIndex={0}
                aria-label={`${f.title} — tap to toggle sound`}
              >
                <video
                  src={`${PUBLIC}/${f.src}`}
                  poster={f.poster ? `${PUBLIC}/${f.poster}` : undefined}
                  autoPlay
                  muted={clipSound !== f.src}
                  loop
                  playsInline
                />
                <div className="ev-film-meta">
                  <span>{f.tag}</span>
                  <strong>{f.title} {clipSound === f.src ? "🔊" : "🔇"}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Villain ── */}
      <section className="ee-section ev-villains">
        <div className="ee-container">
          <div className="ev-sec-kicker red">THE ENEMY GROWS</div>
          <h2 className="ev-sec-title">The Trash-Bot Saga ☠</h2>
          <p className="ev-sec-sub">He gets stronger every time something valuable gets thrown away.</p>
          <div className="ev-villain-row">
            {VILLAINS.map((v) => (
              <div className="ev-villain-card" key={v.name}>
                <img src={`${PUBLIC}/${v.img}`} alt={v.name} />
                <strong>{v.name}</strong>
                <span>{v.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Play + Read + Collect ── */}
      <section className="ee-section ev-more">
        <div className="ee-container ev-more-grid">
          <Link to="/game" className="ev-more-card">
            <img src={`${PUBLIC}/battle-cover.png`} alt="The arcade games" />
            <div><div className="ev-sec-kicker">PLAY</div><strong>The Arcade</strong><p>Mission NYC in 3D, Night Patrol, and more — 5 games, free forever.</p></div>
          </Link>
          <a href="#shop" className="ev-more-card">
            <img src={`${PUBLIC}/ecohero-2.png`} alt="The comics" />
            <div><div className="ev-sec-kicker">READ</div><strong>The Comics</strong><p>Issue #1 — "Trash-Bot Rises." The saga begins in Williamsburg.</p></div>
          </a>
          <a href="#shop" className="ev-more-card">
            <img src={`${PUBLIC}/toy-minifigs.png`} alt="The collectibles" />
            <div><div className="ev-sec-kicker">COLLECT</div><strong>The Toys</strong><p>Vinyl figures & mini-figs — kids get one free on every pickup.</p></div>
          </a>
        </div>
      </section>

      {/* ── The Eco-Hero Shop (moved from the homepage) ── */}
      <section id="shop" className="ee-section ev-shop">
        <div className="ee-container">
          <div className="ev-sec-kicker">🛍️ THE ECO-HERO SHOP</div>
          <h2 className="ev-sec-title">Comics, figures &amp; collectibles</h2>
          <p className="ev-sec-sub">Dropping soon — sign up on the homepage and we'll tell you the second they land.</p>
          <div className="ev-shop-grid">
            {[
              { img: "toy-earthjr.png", badge: "Coming soon", h: "Earth Jr. — Planet Hero", p: "Series 1 collectible vinyl figure with cape & glowing recycle emblem.", price: "$14.99" },
              { img: "toy-minifigs.png", badge: "Kids get 1 FREE 🎁", featured: true, h: "Eco-Allies Mini-Figures", p: "Collect all 5! Binny, Drip, Windy, Sunny & Sprout. Kids get one free on every pickup.", price: "$24.99 / set" },
              { img: "ecohero-2.png", badge: "Coming soon", h: "“Trash-Bot Rises” — Issue #1", p: "The first Everything Eco comic book. Full-color, collector's edition.", price: "$9.99" },
            ].map((s) => (
              <div className={`ev-shop-card${s.featured ? " featured" : ""}`} key={s.h}>
                <div className={`ev-shop-badge${s.featured ? " orange" : ""}`}>{s.badge}</div>
                <div className="ev-shop-img"><img src={`${PUBLIC}/${s.img}`} alt={s.h} /></div>
                <h4>{s.h}</h4>
                <p>{s.p}</p>
                <div className="ev-shop-foot">
                  <span className="ev-shop-price">{s.price}</span>
                  <Link to="/#newsletter" className="ev-shop-btn">🔔 Notify me</Link>
                </div>
              </div>
            ))}
          </div>
          <div className="ev-shop-banner">
            🎁 <strong>Free mini-figure for kids</strong> on every recycling pickup — gotta collect 'em all!
          </div>
        </div>
      </section>

      {/* ── Free downloads ── */}
      <section className="ee-section ev-downloads">
        <div className="ee-container">
          <div className="ev-sec-kicker">FREE STUFF</div>
          <h2 className="ev-sec-title">Wallpapers & printables 🖼️</h2>
          <p className="ev-sec-sub">Right-click (or press-and-hold) to save. Print them, color them, tape them to your wall.</p>
          <div className="ev-dl-grid">
            {DOWNLOADS.map((d) => (
              <a key={d.img} href={`${PUBLIC}/${d.img}`} download className="ev-dl-card">
                <img src={`${PUBLIC}/${d.img}`} alt={d.label} />
                <span>⬇ {d.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ee-section ev-cta">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <img src={`${PUBLIC}/earthjr-thumbs.png`} alt="Earth Jr. giving two thumbs up" className="ev-cta-ej" />
          <h2 className="ev-sec-title">Ready to be a hero for real?</h2>
          <p className="ev-sec-sub">Every pickup is a strike against Trash-Bot. Suit up.</p>
          <div className="ev-cta-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green">📦 Schedule a Free Pickup</Link>
            <Link to="/learn" className="ee-btn ee-btn-blue">🎓 Train at the Eco Academy</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EcoVersePage;
